import { SubscriptionManager } from "../SubscriptionManager";

const ORDERBOOK_EVENT_SCOPE_PREFIX = "event";
const ORDERBOOK_GUESS_SCOPE_PREFIX = "guess";
const ORDERBOOK_SCOPE_SEPARATOR = "::";

export type OrderBookSubscriptionScope = {
    eventNo: string;
    guessNo?: string | null;
};

function normalizeScopeValue( value?: string | null ) {
    return value?.trim() || "";
}

export function createOrderBookEventSubscriptionKey( eventNo: string ) {
    return `${ORDERBOOK_EVENT_SCOPE_PREFIX}${ORDERBOOK_SCOPE_SEPARATOR}${normalizeScopeValue( eventNo )}`;
}

export function createOrderBookGuessSubscriptionKey( eventNo: string, guessNo: string ) {
    return `${ORDERBOOK_GUESS_SCOPE_PREFIX}${ORDERBOOK_SCOPE_SEPARATOR}${
        normalizeScopeValue( eventNo )
    }${ORDERBOOK_SCOPE_SEPARATOR}${normalizeScopeValue( guessNo )}`;
}

export function parseOrderBookSubscriptionKey( subscriptionKey: string ): OrderBookSubscriptionScope | null {
    const segments = subscriptionKey.split( ORDERBOOK_SCOPE_SEPARATOR );
    const [ scopeType, eventNo, guessNo ] = segments;

    if ( segments.length === 1 && scopeType ) {
        return {
            eventNo: scopeType,
        };
    }

    if ( scopeType === ORDERBOOK_EVENT_SCOPE_PREFIX && eventNo ) {
        return {
            eventNo,
        };
    }

    if ( scopeType === ORDERBOOK_GUESS_SCOPE_PREFIX && eventNo && guessNo ) {
        return {
            eventNo,
            guessNo,
        };
    }

    return null;
}

/**
 * 订单盘口订阅管理器
 */
export class OrderBookSubscriptionManager extends SubscriptionManager<string> {
    private blockedGuessSubscriptions = new Set<string>();

    subscribe( subscriptionKey: string, sendFn: ( msg: string ) => void, isConnected: boolean ) {
        const scope = parseOrderBookSubscriptionKey( subscriptionKey );

        if ( !scope ) {
            throw new Error( `Invalid order book subscription key: ${subscriptionKey}` );
        }

        if ( !scope.guessNo ) {
            super.subscribe( subscriptionKey, sendFn, isConnected );
            return;
        }

        const eventSubscriptionKey = createOrderBookEventSubscriptionKey( scope.eventNo );

        if (
            this.activeSubscriptions.has( subscriptionKey )
            || this.pendingSubscriptions.has( subscriptionKey )
            || this.blockedGuessSubscriptions.has( subscriptionKey )
        ) {
            return;
        }

        super.subscribe( eventSubscriptionKey, sendFn, isConnected );

        if ( !isConnected || !this.activeSubscriptions.has( eventSubscriptionKey ) ) {
            this.blockedGuessSubscriptions.add( subscriptionKey );
            return;
        }

        super.subscribe( subscriptionKey, sendFn, isConnected );
    }

    unsubscribe( subscriptionKey: string, sendFn: ( msg: string ) => void ) {
        if ( this.blockedGuessSubscriptions.delete( subscriptionKey ) ) {
            this.pendingSubscriptions.delete( subscriptionKey );
            this.activeSubscriptions.delete( subscriptionKey );
            return;
        }

        super.unsubscribe( subscriptionKey, sendFn );
    }

    handleConnected( sendFn: ( msg: string ) => void ) {
        const subscriptionsToRestore = new Set<string>( [
            ...this.activeSubscriptions,
            ...this.pendingSubscriptions,
            ...this.blockedGuessSubscriptions,
        ] );

        this.clear();
        subscriptionsToRestore.forEach( ( subscriptionKey ) => {
            this.subscribe( subscriptionKey, sendFn, true );
        } );
    }

    handleMessage( data: any, sendFn?: ( msg: string ) => void, isConnected?: boolean ): boolean {
        const handled = super.handleMessage( data, sendFn, isConnected );

        if ( !handled ) {
            return false;
        }

        const ackPayload = this.resolveAckPayload( data );
        if ( ackPayload?.type === "subscribed" && ackPayload.eventNo && !ackPayload.guessNo && sendFn && isConnected ) {
            this.flushBlockedGuessSubscriptions( ackPayload.eventNo, sendFn );
        }

        if ( ackPayload?.guessNo ) {
            this.blockedGuessSubscriptions.delete(
                createOrderBookGuessSubscriptionKey( ackPayload.eventNo, ackPayload.guessNo ),
            );
        }

        return true;
    }

    clear() {
        super.clear();
        this.blockedGuessSubscriptions.clear();
    }

    protected buildSubscribeMessage( subscriptionKey: string ) {
        const scope = parseOrderBookSubscriptionKey( subscriptionKey );

        if ( !scope ) {
            throw new Error( `Invalid order book subscription key: ${subscriptionKey}` );
        }

        return scope.guessNo
            ? { type: "subscribe", eventNo: scope.eventNo, guessNo: scope.guessNo }
            : { type: "subscribe", eventNo: scope.eventNo };
    }

    protected buildUnsubscribeMessage( subscriptionKey: string ) {
        const scope = parseOrderBookSubscriptionKey( subscriptionKey );

        if ( !scope ) {
            throw new Error( `Invalid order book subscription key: ${subscriptionKey}` );
        }

        return scope.guessNo
            ? { type: "unsubscribe_event", eventNo: scope.eventNo, guessNo: scope.guessNo }
            : { type: "unsubscribe_event", eventNo: scope.eventNo };
    }

    protected extractSubscriptionId( data: any ): string | undefined {
        const ackPayload = this.resolveAckPayload( data );
        if ( !ackPayload?.eventNo ) {
            return undefined;
        }

        if ( ackPayload.type === "data_subscribed" && ackPayload.guessNo ) {
            return createOrderBookGuessSubscriptionKey( ackPayload.eventNo, ackPayload.guessNo );
        }

        if ( ackPayload.type !== "subscribed" ) {
            return undefined;
        }

        return ackPayload.guessNo
            ? createOrderBookGuessSubscriptionKey( ackPayload.eventNo, ackPayload.guessNo )
            : createOrderBookEventSubscriptionKey( ackPayload.eventNo );
    }

    private resolveAckPayload( data: any ) {
        if ( data?.payload && typeof data.payload === "object" ) {
            return {
                ...data.payload,
                type: data.type,
            };
        }

        return data;
    }

    private flushBlockedGuessSubscriptions( eventNo: string, sendFn: ( msg: string ) => void ) {
        const waitingGuessSubscriptions = Array.from( this.blockedGuessSubscriptions ).filter( ( subscriptionKey ) => {
            const scope = parseOrderBookSubscriptionKey( subscriptionKey );
            return scope?.eventNo === eventNo && !!scope.guessNo;
        } );

        waitingGuessSubscriptions.forEach( ( subscriptionKey ) => {
            this.blockedGuessSubscriptions.delete( subscriptionKey );
            super.subscribe( subscriptionKey, sendFn, true );
        } );
    }
}
